import { defineStore } from 'pinia'

const TOKEN_KEY = 'wanderingbook_token'
const USER_KEY = 'wanderingbook_user'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY) || null,
    user: JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
    oidcEnabled: false,
    openidOnly: false,
    oidcConnected: false,
    oidcEndSessionUrl: '',
    oidcClientId: '',
    oidcSignInText: '',
    oidcIconUrl: '',
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isAdmin: (state) => state.user?.roles?.includes('admin') || false,
    userId: (state) => state.user?.id || 0,
  },

  actions: {
    async login(username, password) {
      const { api } = await import('@/api')
      const resp = await api.login(username, password)
      this.token = resp.token
      this.user = resp.user
      localStorage.setItem(TOKEN_KEY, resp.token)
      localStorage.setItem(USER_KEY, JSON.stringify(resp.user))
    },

    async startOidcLogin() {
      const { api } = await import('@/api')
      const redirect = localStorage.getItem('wanderingbook_redirect') || '/'
      window.location.href = api.client.defaults.baseURL + '/auth/oidc/login?redirect=' + encodeURIComponent(redirect)
    },

    async completeOidcLogin(token) {
      this.token = token
      localStorage.setItem(TOKEN_KEY, token)
      try {
        const { api } = await import('@/api')
        const user = await api.me()
        this.user = user
        this.oidcConnected = user.oidc_connected
        localStorage.setItem(USER_KEY, JSON.stringify(user))
      } catch {
        this.logout()
      }
    },

    async linkOidc() {
      const { api } = await import('@/api')
      const token = this.token
      let url = api.client.defaults.baseURL + '/auth/oidc/link'
      if (token) {
        url += '?token=' + encodeURIComponent(token)
      }
      window.location.href = url
    },

    async unlinkOidc() {
      const { api } = await import('@/api')
      await api.oidcUnlink()
      this.oidcConnected = false
      if (this.user) {
        this.user.oidc_connected = false
        this.user.oidc_sub = ''
        localStorage.setItem(USER_KEY, JSON.stringify(this.user))
      }
    },

    async oidcLogout() {
      this.token = null
      this.user = null
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      if (this.oidcEndSessionUrl) {
        const redirectUri = window.location.origin + '/wanderingbook/login'
        const logoutUrl = `${this.oidcEndSessionUrl}?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}&client_id=${encodeURIComponent(this.oidcClientId)}`
        window.location.href = logoutUrl
      } else {
        window.location.href = '/wanderingbook/login'
      }
    },

    logout() {
      this.token = null
      this.user = null
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
    },

    async fetchMe() {
      try {
        const { api } = await import('@/api')
        const user = await api.me()
        this.user = user
        this.oidcConnected = user.oidc_connected
        localStorage.setItem(USER_KEY, JSON.stringify(user))
      } catch {
        this.logout()
      }
    },

    async fetchAuthConfig() {
      try {
        const { api } = await import('@/api')
        const config = await api.authConfig()
        this.oidcEnabled = config.oidc_enabled
        this.openidOnly = config.openid_only
        this.oidcEndSessionUrl = config.oidc_end_session_url || ''
        this.oidcClientId = config.oidc_client_id || ''
        this.oidcSignInText = config.oidc_sign_in_text || ''
        this.oidcIconUrl = config.oidc_icon_url || ''
      } catch {
        this.oidcEnabled = false
        this.openidOnly = false
      }
    },
  },
})
